import {
  IconBook2,
  IconCpu,
  IconDoorEnter,
  IconGridDots,
  IconKey,
  IconLogout,
  IconPackage,
  IconShieldLock,
  IconStack2,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { useSession } from "../../auth/SessionProvider";
import { ThemeSwitch } from "./ThemeSwitch";

interface NavItemProps {
  to: string;
  /** Match this destination exactly (the home route would match everything). */
  end?: boolean;
  icon: ReactNode;
  label: string;
  /** One short line saying what the destination is for. */
  detail: string;
}

/** One navigation entry: an icon beside a word, never an icon alone. */
function NavItem({ to, end, icon, label, detail }: NavItemProps): JSX.Element {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `sidebar__item${isActive ? " sidebar__item--active" : ""}`
      }
    >
      <span className="sidebar__item-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="sidebar__item-text">
        <span className="sidebar__item-label">{label}</span>
        <span className="sidebar__item-detail">{detail}</span>
      </span>
    </NavLink>
  );
}

/**
 * The left column: who we are, where you can go, and who you are.
 *
 * Every entry names itself — an icon only ever sits beside its word. The
 * researcher can always answer "where am I" from the highlighted entry and
 * "where does this lead" from the words alone.
 *
 * Returns:
 *     The navigation sidebar.
 */
export function Sidebar(): JSX.Element {
  const { me, signOut } = useSession();

  return (
    <aside className="sidebar">
      <NavLink to="/" className="sidebar__brand" end>
        <img
          className="sidebar__logo"
          src="/brand/logomark-white.svg"
          width={45}
          height={25}
          alt="Neuralk"
        />
        <span className="sidebar__brand-text">
          <span className="sidebar__brand-name">GPU Console</span>
          <span className="sidebar__brand-org">Neuralk Research</span>
        </span>
      </NavLink>

      <nav className="sidebar__nav" aria-label="Main">
        <NavItem
          to="/"
          end
          icon={<IconCpu size={17} stroke={1.75} />}
          label="GPUs"
          detail="Get GPUs, see every machine"
        />
        <NavItem
          to="/jobs"
          icon={<IconStack2 size={17} stroke={1.75} />}
          label="Jobs"
          detail="Run a command, read the log"
        />
        {/* Not IconCpu — the chip is already the GPU entry's, and two chips
            would say the two fleets are the same thing, which is the one
            mistake this feature exists to avoid. A grid of identical dots is
            what cores actually are here: a quantity of interchangeable units,
            none of which has a name, a holder or an activity of its own. */}
        <NavItem
          to="/cpu"
          icon={<IconGridDots size={17} stroke={1.75} />}
          label="CPU"
          detail="Cores with a shell, or a command"
        />
        <NavItem
          to="/environments"
          icon={<IconPackage size={17} stroke={1.75} />}
          label="Environments"
          detail="What workspaces run — add your own"
        />
        {/*
          Shown to everybody, because it belongs to everybody: reaching that
          machine comes with being approved, exactly like the GPUs do
          (`docs/AUTH.md` §2, ADR-0011). No entitlement is consulted here —
          hiding the entry from the people who have not been declared to IDRIS
          would hide the MLflow tunnel, which is the half they all need.
        */}
        <NavItem
          to="/relay"
          icon={<IconDoorEnter size={17} stroke={1.75} />}
          label="Training elsewhere"
          detail="Our MLflow from any cluster"
        />
        <NavItem
          to="/docs"
          icon={<IconBook2 size={17} stroke={1.75} />}
          label="Guide"
          detail="How all of this works"
        />
        <NavItem
          to="/account"
          icon={<IconKey size={17} stroke={1.75} />}
          label="Account"
          detail="Your SSH key"
        />
        {me.is_admin ? (
          <NavItem
            to="/admin"
            icon={<IconShieldLock size={17} stroke={1.75} />}
            label="Admin"
            detail="Approvals and the emergency stop"
          />
        ) : null}
      </nav>

      <div className="sidebar__identity">
        <p className="sidebar__who">
          <span className="sidebar__who-name">{me.username}</span>
          {me.email ? (
            <span className="sidebar__who-mail">{me.email}</span>
          ) : null}
        </p>
        <ThemeSwitch />
        <button type="button" className="sidebar__signout" onClick={signOut}>
          <IconLogout size={14} aria-hidden="true" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
